import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { pickSeed, pickVariant } from '../engine/variants';
import type { OrgConfig, ScenarioDoc } from '../engine/types';

interface PriorAttempt {
  id: string;
  scenarioId: string;
  submittedAt: string;
  voided?: boolean;
  /** The persona the *server* assigned, which is the one §14.2 excludes next time. */
  result?: { passed?: boolean; assignedPersona?: string };
}

/**
 * The gate in front of a certification attempt (§14.3, §14.5, §14.6).
 *
 * The rep is told what they are about to sit and what it costs — attempts are
 * capped and there is a cooldown — but not which scenario, which persona, or
 * anything else that would let them prepare a specific path. The variant is
 * chosen here, at random, excluding the one they saw last.
 */
export function SummativeGate({
  orgId, uid, stage, granted, config, scenarios, rehearsal = false, onBegin, onDone,
}: {
  orgId: string;
  uid: string;
  /** The role-scoped stage key (§4.1) — a setter's cap and cooldown are not a
   *  closer's, and a hybrid rep certifies each track on its own budget. */
  stage: string;
  /** §13.6 — attempts a manager has granted beyond the cap, for this stage. */
  granted: number;
  config: OrgConfig;
  scenarios: ScenarioDoc[];
  /** §13.4 — a manager or admin walking the ladder. The call is identical; what
   *  differs is that it earns nothing, so the cap and the cooldown that protect
   *  a credential have nothing to protect and do not apply. */
  rehearsal?: boolean;
  onBegin: (choice: { scenarioId: string; seed: number }) => void;
  onDone: () => void;
}) {
  const [prior, setPrior] = useState<PriorAttempt[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(
          collection(db, `orgs/${orgId}/attempts`),
          where('uid', '==', uid),
          where('stage', '==', stage),
          orderBy('submittedAt', 'desc'),
          limit(20)
        ));
        if (cancelled) return;
        setPrior(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<PriorAttempt, 'id'>) })));
      } catch (err) {
        console.error('could not read prior attempts', err);
        if (!cancelled) setError('Your attempt history could not be read.');
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, uid, stage]);

  // §13.6 — a voided attempt is not spent. An admin void hands the attempt back
  // without anyone needing to grant a replacement.
  const spent = useMemo(() => (prior ?? []).filter((a) => !a.voided), [prior]);
  const cap = config.certification.maxSummativeAttempts + Math.max(0, granted);
  const remaining = Math.max(0, cap - spent.length);
  const alreadyPassed = spent.some((a) => a.result?.passed);

  const cooldownMinutes = config.certification.attemptCooldownMinutes;
  const lastAt = spent[0]?.submittedAt ? Date.parse(spent[0].submittedAt) : null;
  const waitMinutes = lastAt
    ? Math.ceil((cooldownMinutes * 60000 - (Date.now() - lastAt)) / 60000)
    : 0;
  const cooling = waitMinutes > 0;

  /**
   * §14.3 — one variant at random, excluding the one they saw last; §14.2 — a
   * seed whose persona is not the one they faced last time. Both exclusions are
   * decided here, at the gate, so nothing about the attempt is visible from the
   * ladder before it starts.
   */
  const begin = () => {
    const chosenId = pickVariant(scenarios.map((s) => s.id), spent[0]?.scenarioId ?? null);
    const chosen = scenarios.find((s) => s.id === chosenId);
    if (!chosen) return;
    onBegin({
      scenarioId: chosen.id,
      seed: pickSeed(chosen.eligiblePersonas, spent[0]?.result?.assignedPersona ?? null),
    });
  };

  if (!prior && !error) {
    return <main className="centered"><p className="muted">Loading…</p></main>;
  }

  // Nothing gates a rehearsal. The cap exists so a certification cannot be
  // ground out by repetition; there is no certification here to grind out.
  const blocked = !rehearsal && (remaining === 0 || cooling);

  return (
    <main className="shell">
      <header className="drill-head">
        <button className="ghost small" onClick={onDone}>Back</button>
      </header>

      <div className="card gate">
        <h1>{rehearsal ? 'Certification attempt — rehearsal' : 'Certification attempt'}</h1>
        <p className="muted">
          One call, scored on the server. You will not see the meter, you will not
          be told how an answer landed, and there is no going back a turn.
        </p>

        <ul className="gate-facts">
          {rehearsal ? (
            <>
              <li>As many attempts as you like — this one earns no certification</li>
              <li>Scored by the same code a rep’s attempt is scored by</li>
            </>
          ) : (
            <>
              <li>
                <strong>{remaining}</strong> of {cap} attempts left
                {granted > 0 && (
                  <span className="muted small">
                    {' '}— {config.certification.maxSummativeAttempts} standard,
                    {' '}{granted} granted by your manager
                  </span>
                )}
              </li>
              <li>{cooldownMinutes} minutes between attempts</li>
            </>
          )}
          <li>You do not choose the prospect, and you do not choose the call</li>
        </ul>

        {error && <p className="error" role="alert">{error}</p>}

        {alreadyPassed && !rehearsal && (
          <p className="pass-note">
            You have already passed. Your manager confirms the certification itself.
          </p>
        )}

        {remaining === 0 && !alreadyPassed && !rehearsal && (
          <p className="warn">
            You have used every attempt{granted > 0 ? ', granted ones included' : ''}.
            Your manager can grant another.
          </p>
        )}

        {cooling && remaining > 0 && !rehearsal && (
          <p className="warn">
            Another attempt is available in about {waitMinutes} minute{waitMinutes === 1 ? '' : 's'}.
          </p>
        )}

        <button className="primary wide" onClick={begin} disabled={blocked || !scenarios.length}>
          {scenarios.length ? 'Begin' : 'No certification content published yet'}
        </button>
      </div>

      {spent.length > 0 && (
        <section className="review">
          <h2>Your attempts</h2>
          <ul className="review-list">
            {spent.map((a) => (
              <li key={a.id} className="review-item">
                <span className="review-label">
                  {new Date(a.submittedAt).toLocaleString()}
                </span>
                <span className={`pill ${a.result?.passed ? 'pill-passed' : 'pill-severe'}`}>
                  {a.result ? (a.result.passed ? 'Passed' : 'Not passed') : 'Scoring'}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </main>
  );
}
